import { IconTrash } from "@tabler/icons-react";
import {
	Button,
	Descriptions,
	Flex,
	Form,
	Input,
	Modal,
	Skeleton,
	Space,
	Tag,
	Typography,
} from "antd";
import { useState } from "react";
import {
	useAddOrgDomain,
	useOrgDomains,
	useRemoveOrgDomain,
	useRequestDomainVerification,
	useVerifyDomain,
} from "@/common/artemis-operations/organization";
import { confirmDestructive } from "@/common/confirm";
import { useNotify } from "@/common/use-notify";
import {
	BaseContainer,
	COPYABLE,
	CustomIcon,
	QueryGuard,
} from "@/components/base";
import { CustomList } from "@/components/custom-list";
import { CustomDrawer } from "@/components/layout/workspace/custom-drawer";

function AddDomainModal({
	open,
	onClose,
}: {
	open: boolean;
	onClose: () => void;
}) {
	const [form] = Form.useForm();
	const notify = useNotify();
	const addDomain = useAddOrgDomain();

	function handleClose() {
		form.resetFields();
		onClose();
	}

	function handleOk() {
		form.validateFields().then((values) => {
			addDomain.mutate(values.domain, {
				onSuccess: () => {
					notify.success("Domain added. Verify it to enable auto-join.");
					handleClose();
				},
				onError: (err) =>
					notify.error((err as Error).message || "Failed to add domain."),
			});
		});
	}

	return (
		<Modal
			title="Add domain"
			open={open}
			onCancel={handleClose}
			onOk={handleOk}
			okText="Add"
			confirmLoading={addDomain.isPending}
			destroyOnHidden
		>
			<Form form={form} layout="vertical" style={{ marginTop: 16 }}>
				<Form.Item
					name="domain"
					label="Domain"
					rules={[{ required: true, message: "Enter a domain" }]}
				>
					<Input placeholder="acme.com" />
				</Form.Item>
			</Form>
		</Modal>
	);
}

type Domain = {
	id: string;
	domain: string;
	verifiedAt?: unknown;
	verificationToken?: string | null;
};

/** DNS-TXT verification flow for a single domain, in its own drawer. */
function DomainVerifyDrawer({
	domain,
	open,
	onClose,
}: {
	domain: Domain;
	open: boolean;
	onClose: () => void;
}) {
	const notify = useNotify();
	const requestVerify = useRequestDomainVerification();
	const verify = useVerifyDomain();

	const txtValue = domain.verificationToken
		? `stratonext-verify=${domain.verificationToken}`
		: "";

	function handleRequest() {
		requestVerify.mutate(domain.id, {
			onError: (err) =>
				notify.error((err as Error).message || "Failed to start verification."),
		});
	}

	function handleVerify() {
		verify.mutate(domain.id, {
			onSuccess: () => {
				notify.success("Domain verified.");
				onClose();
			},
			onError: (err) =>
				notify.error(
					(err as Error).message ||
						"Verification failed. Check the DNS record and retry.",
				),
		});
	}

	return (
		<CustomDrawer
			title={`Verify ${domain.domain}`}
			open={open}
			onClose={onClose}
		>
			<Flex vertical gap={16}>
				{txtValue ? (
					<>
						<Typography.Paragraph type="secondary" style={{ margin: 0 }}>
							Add the following TXT record at your DNS provider to prove
							ownership of {domain.domain}, then click “Check DNS”.
						</Typography.Paragraph>
						<Descriptions
							column={1}
							bordered
							size="small"
							items={[
								{ key: "type", label: "Record type", children: "TXT" },
								{
									key: "name",
									label: "Name",
									children: (
										<Typography.Text
											copyable={COPYABLE}
											style={{ wordBreak: "break-all" }}
										>
											{`_stratonext-verify.${domain.domain}`}
										</Typography.Text>
									),
								},
								{
									key: "value",
									label: "Value",
									children: (
										<Typography.Text
											copyable={COPYABLE}
											style={{ wordBreak: "break-all" }}
										>
											{txtValue}
										</Typography.Text>
									),
								},
							]}
						/>
						<Typography.Text type="secondary" style={{ fontSize: 12 }}>
							DNS changes can take a few minutes to propagate.
						</Typography.Text>
						<Button
							type="primary"
							loading={verify.isPending}
							onClick={handleVerify}
						>
							Check DNS
						</Button>
					</>
				) : (
					<>
						<Typography.Paragraph type="secondary">
							Generate a DNS TXT record to prove you own {domain.domain}. Once
							it's added to your DNS settings, we can verify it.
						</Typography.Paragraph>
						<Button
							type="primary"
							loading={requestVerify.isPending}
							onClick={handleRequest}
						>
							Generate DNS record
						</Button>
					</>
				)}
			</Flex>
		</CustomDrawer>
	);
}

function DomainItem({ domain }: { domain: Domain }) {
	const notify = useNotify();
	const remove = useRemoveOrgDomain();
	const [verifyOpen, setVerifyOpen] = useState(false);

	const isVerified = !!domain.verifiedAt;

	function handleRemove() {
		confirmDestructive({
			title: "Remove domain",
			content: `Remove ${domain.domain}?`,
			onOk() {
				remove.mutate(domain.id, {
					onSuccess: () => notify.success("Domain removed."),
					onError: (err) =>
						notify.error((err as Error).message || "Failed to remove domain."),
				});
			},
		});
	}

	return (
		<Flex
			justify="space-between"
			align="center"
			gap={8}
			style={{ width: "100%" }}
		>
			<Flex align="center" gap={8} style={{ minWidth: 0 }}>
				<Typography.Text strong ellipsis={{ tooltip: domain.domain }}>
					{domain.domain}
				</Typography.Text>
				<Tag color={isVerified ? "green" : "default"}>
					{isVerified ? "Verified" : "Unverified"}
				</Tag>
			</Flex>
			<Space>
				{!isVerified && (
					<Button
						size="small"
						type="primary"
						onClick={() => setVerifyOpen(true)}
					>
						Verify
					</Button>
				)}
				<Button
					icon={<CustomIcon icon={IconTrash} />}
					type="text"
					danger
					onClick={handleRemove}
					loading={remove.isPending}
				/>
			</Space>
			<DomainVerifyDrawer
				domain={domain}
				open={verifyOpen}
				onClose={() => setVerifyOpen(false)}
			/>
		</Flex>
	);
}

/** Lists an org's domains with verified badges and a DNS-TXT verification flow.
 * The Add action is owned by the page-level action bar (controlled via addOpen/onCloseAdd). */
export function OrganizationDomains({
	addOpen,
	onCloseAdd,
}: {
	addOpen: boolean;
	onCloseAdd: () => void;
}) {
	const domains = useOrgDomains();

	return (
		<BaseContainer title="Domains">
			<QueryGuard
				isPending={domains.isPending}
				isError={domains.isError}
				isNull={false}
				pending={<Skeleton active />}
			>
				<CustomList
					data={domains.data ?? []}
					renderItem={(d) => <DomainItem domain={d} />}
				/>
			</QueryGuard>

			<AddDomainModal open={addOpen} onClose={onCloseAdd} />
		</BaseContainer>
	);
}
